import {Component, OnDestroy, OnInit} from '@angular/core';
import {count, firstValueFrom, Observable, Subscription} from "rxjs";
import {DjangoApiService} from "../../../../shared/services/django-api.service";
import {SnackbarService} from "../../../../shared/services/snackbar.service";
import {ActivatedRoute} from "@angular/router";
import {LanguageService} from "../../../../shared/services/language.service";
import {LangChangeEvent, TranslateModule, TranslateService} from "@ngx-translate/core";
import {MatProgressSpinner} from "@angular/material/progress-spinner";
import {CardsBasicComponent} from "../../../../shared/components/cards/cards-basic/cards-basic.component";
import {MatList, MatListItem} from "@angular/material/list";
import {MatIcon, MatIconModule} from "@angular/material/icon";
import {FormsModule} from "@angular/forms";
import {MatFormField, MatLabel} from "@angular/material/form-field";
import {MatInput, MatInputModule} from "@angular/material/input";
import {
  MatDatepicker,
  MatDatepickerInput,
  MatDatepickerModule,
  MatDatepickerToggle
} from "@angular/material/datepicker";
import {MatButton, MatIconButton} from "@angular/material/button";
import {DatePipe} from "@angular/common";
import {provideNativeDateAdapter} from "@angular/material/core";
import {MatRadioButton, MatRadioGroup} from "@angular/material/radio";
import {PdfForPageService} from "../../../../shared/services/pdfMake/pdf-for-page.service";
import {PdfService} from "../../../../shared/services/pdfMake/pdf.service";
import {MatMenuModule} from "@angular/material/menu";
import {MatDialog} from "@angular/material/dialog";
import {DialogConfirmComponent} from "../../../../shared/components/dialogs/dialog-confirm/dialog-confirm.component";

@Component({
  selector: 'app-report-list',
  standalone: true,
  imports: [
    TranslateModule,
    CardsBasicComponent,
    MatList,
    MatListItem,
    MatIcon,
    FormsModule,
    MatFormField,
    MatInput,
    MatDatepickerToggle,
    MatDatepicker,
    MatDatepickerInput,
    MatButton,
    MatProgressSpinner,
    MatIconButton,
    MatLabel,
    MatDatepickerModule,
    MatInputModule,
    MatIconModule,
    MatRadioButton,
    MatRadioGroup,
    MatMenuModule
  ],
  providers: [provideNativeDateAdapter(), DatePipe],
  templateUrl: './report-list.component.html',
  styleUrl: './report-list.component.css'
})
export class ReportListComponent implements OnInit, OnDestroy {
  reportListData: any[] = [];
  isFormVisible: boolean = false;
  isLoading: boolean = false;
  isEditMode: boolean = false;
  reportItem!: any;
  productId!: any;
  pageId!: any;
  pageList: any[] = [];
  selectedPage: any;
  pdfProductSlug!: string;
  pdfPageSlug!: string;
  pdfDate!: string;
  currentLanguage!: string;
  langChangeSubscription: Subscription | null = null;

  private subscriptions: Subscription = new Subscription();

  constructor(
    private _apiService: DjangoApiService,
    private _snackbarService: SnackbarService,
    private _datePipe: DatePipe,
    public _route: ActivatedRoute,
    private _languageService: LanguageService,
    private _translate: TranslateService,
    private _pdfForPageService: PdfForPageService,
    private _pdfService: PdfService,
    private _dialog: MatDialog) {
  }




  ngOnInit(): void {
    this.getRouteParams();
    this.loadReportListData();
    this.createEmptyReport();
  }

  private getRouteParams(): void {
    this._route.params.subscribe(params => {
      this.productId = this._route.snapshot.parent?.params['productId'];
      this.setupLanguageSubscription();
    });
  }


  private setupLanguageSubscription(): void {
    this.currentLanguage = this._languageService.getLanguage();
    this.subscriptions.add(
      this.langChangeSubscription = this._translate.onLangChange.subscribe((event: LangChangeEvent) => {
        this.currentLanguage = event.lang;
      })
    );
  }


  loadReportListData() {
    this.subscriptions.add(
      this._apiService.fetchReportList(this.productId).subscribe({
        next: (response: any) => {
          this.reportListData = response;
          console.log('reportListData', this.reportListData);

          this.loadPageList();
        },
        error: (error) => {
          console.error('There was an error fetching the user information', error);
        }
      })
    );
  }

  private loadPageList() {
    this.subscriptions.add(
      this._apiService.fetchPageList(this.productId)
        .subscribe({
          next: (res: any) => {
            this.pageList = res
          },
          error: (error) => {
            console.error('There was an error fetching the pages', error);
          }
        })
    )
  }


  createEmptyReport() {
    const translations: { [key: string]: { title: string, comment: string, pdf_file: string } } = {};

    this._languageService.supportedLangs.forEach(lang => {
      translations[lang] = {
        title: '',
        comment: '',
        pdf_file: '',
      };
    });

    this.reportItem = {
      product: this.productId,
      page_template: this.pageId,
      date: '',
      translations: translations,
    };
  }


  toggleFormVisibility(): void {
    this.isFormVisible = !this.isFormVisible;
    this.resetFormData();
  }


  private resetFormData(): void {
    this.createEmptyReport();
    this.isEditMode = false;
  }


  private formatDate(date: Date): string {
    return this._datePipe.transform(date, 'yyyy-MM-dd') || '';
  }


  savePhase(): void {
    if (this.isEditMode) {
      this.updateReport();
    } else {
      this.createReport();
    }
  }


  private applyDateFormatting(report: any): any {
    return {
      ...report,
      date: this.formatDate(new Date(report.date!))
    };
  }


  private handleSubscription(operation: Observable<any>): void {
    this.isLoading = true;
    this.subscriptions.add(
      operation.subscribe({
        next: (response: any) => {
          this.isFormVisible = false;
          this.isLoading = false;
          this._snackbarService.show(response.message);
          this.loadReportListData();
        },
        error: (error) => {
          console.error('There was an error', error);
          this.isLoading = false;
        }
      })
    );
  }


  createReport(): void {
    this.reportItem.product = this.productId;
    const formatedReport = this.applyDateFormatting(this.reportItem);
    this.handleSubscription(
      this._apiService.createReport(formatedReport)
    );
  }


  updateReport(): void {
    const formattedPhase = this.applyDateFormatting(this.reportItem);
    this.handleSubscription(
      this._apiService.updateReportDetail(formattedPhase)
    );
  }

  confirmDelete(id: number, event: Event) {
    event.stopPropagation();
    event.preventDefault();

    DialogConfirmComponent.openDeleteDialog(this._dialog, this._translate).subscribe(result => {
      if (result) {
        this.deleteReport(id);
      }
    });
  }


  deleteReport(id: any): void {
    this.handleSubscription(
      this._apiService.deleteReport(id)
    );
  }


  editReport(report: any): void {
    this.reportItem = {...report};
    if (this.reportItem.page_template) {
      this.selectedPage = this.pageList.find(page => page.id === this.reportItem.page_template);
    }
    this.isFormVisible = true;
    this.isEditMode = true;
  }

  onSelectedPageChange(selectedPage: any) {
    // Update the `use_for_statistics` property for all benchmarks
    this.reportItem.page_template = selectedPage.id
  }

  /*
  public generatePdfForPage(report: any, event: MouseEvent) {
    event.stopPropagation();
    this.pdfDate = report.date;
    this.loadPage(report);
  }

   */

  // ─── NEUE METHODE: PDFs für alle Sprachen generieren und im Report speichern ─────────────
  public async generatePdfForPage(report: any, event: MouseEvent): Promise<void> {
    event.stopPropagation();
    this.pdfDate = report.date;
    this.isLoading = true;

    try {
      // Produktdetails laden, damit die Header-Daten stimmen
      const product = await firstValueFrom(this._apiService.fetchProductDetail(report.product));
      const languages = this._languageService.supportedLangs; // z. B. ['en', 'de']

      // Für jede Sprache sequentiell abarbeiten:
      for (const lang of languages) {
        // Für die jeweilige Sprache: Reset und Initialisierungen
        this._pdfService.reset();
        this._pdfService.initializeCurrentLanguage(lang);
        if (report.translations[lang].comment) {
          this._pdfService.initializeComment(report.translations[lang].comment);
        }
        // Header: Produkt-Titel, Report-Titel und Datum
        this._pdfForPageService.initializeHeader(
          product.translations[lang]?.title || '',
          report.translations[lang]?.title || '',
          report.date
        );

        // Warten, bis das PDF für diese Sprache erzeugt wurde
        const pdfBase64 = await this._pdfForPageService.generatePdfForPageBinary(report, lang, product);
        // Speichern des erzeugten PDFs im Report
        report.translations[lang].pdf_file = pdfBase64;
      }

      // Nach der Generierung aller PDFs den Report im Backend updaten
      await firstValueFrom(this._apiService.updateReportDetail(report));
      this._snackbarService.show("PDFs generated successfully");
      this.loadReportListData();
    } catch (error) {
      console.error("Error generating PDFs", error);
    } finally {
      this.isLoading = false;
    }
  }


  // ─── Methode zum Öffnen des PDFs (jeweils in der entsprechenden Sprache) ─────────────
  openPdf(report: any, lang: string, event: MouseEvent): void {
    event.stopPropagation();
    const base64Pdf = report.translations[lang].pdf_file;
    if (base64Pdf) {
      const pdfWindow = window.open('');
      pdfWindow?.document.write(
        `<iframe width="100%" height="100%" src="data:application/pdf;base64,${base64Pdf}"></iframe>`
      );
    }
  }

  sendReport(report: any, event: MouseEvent): void {
    event.stopPropagation(); // Verhindert, dass andere Klick-Events getriggert werden
    this._apiService.sendReportToSubscribers(report.id).subscribe({
      next: (res: any) => {
        console.log('Report sent to subscribers successfully', res);
        this._snackbarService.show('Report sent to subscribers successfully');
      },
      error: (err: any) => {
        console.error('Error sending report', err);
        this._snackbarService.show('Error sending report');
      }
    });
  }


  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  protected readonly count = count;
}
